import React, { useState, useRef, useEffect } from "react";
import { LucideIcon } from "lucide-react";
import { Button } from "./button";

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  options: DropdownOption[];
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  allLabel?: string;
  icon?: LucideIcon;
}

export function Dropdown({
  options,
  value,
  onChange,
  placeholder = "Select...",
  allLabel = "All",
  icon,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption ? selectedOption.label : allLabel;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const handleSelect = (optionValue: string | undefined) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="white"
        size="small"
        icon={icon}
      >
        {displayLabel}
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="py-1">
            {/* All/Reset Option */}
            <button
              onClick={() => handleSelect(undefined)}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${
                !value
                  ? "bg-slate-50 font-medium text-default-600"
                  : "text-slate-700"
              }`}
            >
              {allLabel}
            </button>

            {/* Divider */}
            {options.length > 0 && (
              <div className="border-t border-gray-200 my-1"></div>
            )}

            {/* Options */}
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${
                  value === option.value
                    ? "bg-slate-50 font-medium text-default-600"
                    : "text-slate-700"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
